/**
 * use-ai.ts — Hook & kiểu dữ liệu cho nhóm trang "AI" (eCDP).
 * Bọc các endpoint backend dưới /api/v1/ai (base client đã có /api/v1).
 * Chỉ dùng lại `api` + TanStack Query + `useApiQuery` (không sửa file chung).
 */
import { api } from '@/lib/api-client'
import { useApiQuery } from '@/hooks/use-api'
import type { AiSource, BotEffective } from '@/lib/ai-credits'

// ── Query keys ──────────────────────────────────────────────────────
export const aiKeys = {
  config: ['ai', 'config'] as unknown[],
  usage: (days: number): unknown[] => ['ai', 'usage', days],
  replyRuns: (limit: number): unknown[] => ['ai', 'reply-runs', limit],
  trace: (runId: string | null): unknown[] => ['ai', 'trace', runId],
  scenarios: ['ai', 'scenarios'] as unknown[],
  knowledgeGaps: (status: string): unknown[] => ['ai', 'knowledge-gaps', status],
  channelOverrides: ['ai', 'channel-overrides'] as unknown[],
  customModels: ['ai', 'custom-models'] as unknown[],
  bots: ['ai', 'bots'] as unknown[],
  dataSources: ['ai', 'data-sources'] as unknown[],
  pendingOrders: ['ai', 'pending-actions', 'create_order'] as unknown[],
}

// ── Kiểu dữ liệu ────────────────────────────────────────────────────
export interface ProviderModel {
  title: string
  value: string
}
export interface ProviderDef {
  id: string
  name: string
  baseUrl: string
  models: ProviderModel[]
  supportsCaching: boolean
  primary: boolean
}

export type AiMode = 'manual' | 'suggest' | 'auto'

export type TaskOverride = { provider?: string | null; model?: string | null }
export type TaskOverrides = Record<string, TaskOverride>

export interface AiConfig {
  orgId: string
  provider: string
  model: string
  maxDaily: number
  enabled: boolean
  taskOverrides?: TaskOverrides | null
  defaultAiMode: AiMode
  autoReplyEnabled: boolean
  debounceSeconds: number
  prefilterKeywords: string | null
  /** Nhóm: chỉ trả lời khi được nhắc tên. */
  groupRequireMention: boolean
  /** Tên gọi coi là được nhắc, cách nhau bằng dấu phẩy. */
  mentionNames: string | null
  verifyBeforeSend?: boolean
  hasOpenaiKey: boolean
  hasMinimaxKey: boolean
  hasAnthropicKey: boolean
  hasGeminiKey: boolean
  /** Có key hay chưa theo id nền tảng (openai, anthropic, gemini, xai, deepseek, minimax cũ). */
  providerKeys?: Record<string, boolean>
  availableProviders: ProviderDef[]
  isAfterHours: boolean
  /** Khung ba tầng — Platform khoá AI của tổ chức (tổ chức không sửa được). Server cũ: undefined. */
  platformAiDisabled?: boolean
  /** Khung ba tầng — trần lượt / ngày do Platform đặt (null = không đặt). Server cũ: undefined. */
  platformMaxDaily?: number | null
}

export interface AiUsage {
  callsToday: number
  tokensIn: number
  tokensOut: number
  cacheReadTokens: number
  costUsd: number
  maxDaily: number
  remaining: number
  enabled: boolean
  byType: Record<string, number>
  days: number
  /** AI hai chế độ — server mới: tách theo nguồn. */
  bySource?: Array<{ source: AiSource; calls: number; tokensIn: number; tokensOut: number; costUsd: number; credits: number }>
  /** AI hai chế độ — server mới: theo tác vụ + model + nguồn. */
  byFeature?: Array<{ feature: string; model: string | null; source: AiSource; calls: number; credits: number }>
  creditsCharged?: number
}

export interface ReplyRun {
  id: string
  conversationId: string | null
  mode: string | null
  status: string | null
  handoff: boolean | null
  confidence: number | null
  latencyMs: number | null
  createdAt: string
}

export interface AiTrace {
  id: string
  conversationId: string | null
  aiReplyRunId: string | null
  step: string
  level: string
  payload: unknown
  latencyMs: number | null
  createdAt: string
  expiresAt: string | null
}

export interface ScenarioMeta {
  id: string
  key: string
  name: string
  description: string
  loadMode: string
  enabled: boolean
  priority: number
  version: number
  updatedAt: string
  /** Khung ba tầng — phạm vi kỹ năng: null/undefined = mọi nhân viên AI; mảng id = chỉ các nhân viên đó. */
  botIds?: string[] | null
}

export interface ScenarioDetail extends ScenarioMeta {
  content: string
  triggerHints: string | null
  updatedBy: string | null
  createdAt: string
}

export interface ScenarioInput {
  key?: string
  name: string
  description: string
  content: string
  loadMode?: string
  triggerHints?: string | null
  priority?: number
  enabled?: boolean
  /** Khung ba tầng — null = mọi nhân viên AI; [] = chưa áp nhân viên nào. */
  botIds?: string[] | null
}

export type GapStatus = 'open' | 'resolved' | 'dismissed'

export interface KnowledgeGap {
  id: string
  conversationId: string | null
  contactId: string | null
  aiReplyRunId: string | null
  gapType: string
  question: string
  suggestion: string | null
  status: GapStatus
  occurrences: number
  resolvedBy: string | null
  notes: string | null
  lastSeenAt: string
  createdAt: string
  updatedAt: string
}

// Kênh (channel account) + override AI theo kênh
export interface ChannelInfo {
  id: string
  displayName: string | null
  platform: number
  status: string
}
export interface ChannelOverride {
  provider?: string
  model?: string
}
export type ChannelOverrides = Record<string, ChannelOverride>

// Model tuỳ chỉnh theo provider (catalog dạng dữ liệu — thêm không cần restart)
export type CustomModels = Record<string, ProviderModel[]>

// AI Bot ("con AI") — mỗi bot một bộ cấu hình riêng, gán theo kênh
export interface ToolConfigEntry {
  enabled: boolean
  /** search_knowledge: knowledgeBaseIds = kho tri thức được tra ([] = mọi kho); categoryIds cũ của kho chỉ còn để đọc. */
  guardrail: { categoryIds: string[]; knowledgeBaseIds?: string[]; legacyCategoryIds?: string[] }
}
export type AiToolName = 'search_products' | 'search_knowledge'
/** Cấu hình công cụ + nguồn dữ liệu bán hàng của bot (ai_bots.tools_json). */
export interface AiBotToolsJson {
  search_products?: ToolConfigEntry
  search_knowledge?: ToolConfigEntry
  /** Chưa có = mọi nguồn sản phẩm đang hoạt động, chưa tra đơn. Khoá: evotech | import | manual… */
  dataSources?: { products: string[]; orders: string[] }
  /** Chỉ tư vấn sản phẩm còn hàng. */
  inStockOnly?: boolean
  /** Công cụ "Xem điểm / hạng thành viên Evotech" (đọc trực tiếp, cần uỷ quyền). */
  memberLookup?: boolean
  /** Công cụ "Tạo đơn nháp cho khách" (Evotech) — cần uỷ quyền ghi riêng (aiOrderAuthorized). */
  orderCreate?: boolean
  /** Trần tổng tiền một đơn nháp AI được tạo (VND). Chưa đặt = mặc định 5.000.000đ. */
  orderMaxValue?: number
  /** Phương thức thanh toán AI được dùng khi tạo đơn nháp. Chưa đặt = mặc định chỉ COD. */
  orderPayTypes?: ('COD' | 'Transfer')[]
  /** Hạn đơn nháp AI tạo trước khi tự huỷ (phút, 5–1440). Chưa đặt = mặc định 60. */
  orderDraftTtlMinutes?: number
}

/** Nguồn dữ liệu bán hàng ĐANG HOẠT ĐỘNG (GET /ai/data-sources). */
export interface AiDataSource {
  key: string
  label: string
  kind: 'integration' | 'local'
  products: { active: number; inStock: number; stockTracked: number } | null
  orders: { total: number } | null
  lastSyncAt: string | null
  /** unauthorized = ứng dụng đã kết nối nhưng tổ chức chưa uỷ quyền cho AI đọc dữ liệu. */
  status: 'active' | 'unauthorized'
}

export function useAiDataSources() {
  // Nguồn được kết nối ở màn Kết nối ứng dụng (mở ở tab mới) → quay lại tab là cập nhật.
  return useApiQuery<{ sources: AiDataSource[] }>(aiKeys.dataSources, '/ai/data-sources', undefined, { refetchOnWindowFocus: true })
}
export interface AiBot {
  id: string
  name: string
  avatarEmoji: string | null
  description: string | null
  enabled: boolean
  provider: string | null
  model: string | null
  personaPrompt: string | null
  playbookPrompt: string | null
  toolsJson: AiBotToolsJson | null
  channelAccountIds: string[]
  /** Lịch làm việc RIÊNG của bot — bật thì đè lịch chung của tổ chức. */
  scheduleEnabled: boolean
  scheduleStartHour: number | null
  scheduleEndHour: number | null
  scheduleDaytimeMode: string | null
  scheduleNighttimeMode: string | null
  // ── Nhân viên AI kiểu SaleSmartly ──
  kind: AiBotKind
  avatarUrl: string | null
  teamId: string | null
  onlineStatus: OnlineStatus
  /** Số hội thoại chưa kết thúc tối đa mỗi ngày (0 = không tự phân bổ) — dành cho tự phân bổ sau này. */
  maxConcurrentDaily: number
  /** Số khách (hội thoại) bot đã tiếp nhận hôm nay, giờ VN — chỉ có ở danh sách bot. */
  intakeToday?: number
  /** "Tất cả tài khoản": phục vụ mọi kênh chưa giao riêng cho bot khác. */
  allChannels: boolean
  /** Thông tin kết nối AI bên thứ ba — bí mật đã che ("••••abcd"). */
  externalConfig: Record<string, string> | null
  externalConfigBroken?: boolean
  webhookReceiveUrl: string | null
  hasWebhookToken?: boolean
  /** URL agent webhook gọi để trả lời khách. */
  webhookReplyUrl?: string | null
  /** AI hai chế độ: nhà cung cấp/model/nguồn thật đang dùng + sẵn sàng hay tạm dừng (server mới). */
  effective?: BotEffective | null
  /** Khung ba tầng — nhân viên AI mặc định (phụ trách mọi kênh chưa giao). Server cũ: undefined → dùng `allChannels`. */
  isDefault?: boolean
  /** Khung ba tầng — chế độ cả ngày khi không bật lịch. */
  defaultMode?: AiMode
  /** Khung ba tầng — tình trạng trả lời thật (như GET /ai/bots/:id/status). */
  status?: AiBotStatus | null
  createdAt: string
  updatedAt: string
}
/** GET /ai/bots/:id/status (và `status` trong danh sách bot) — khung ba tầng. */
export interface AiBotStatus {
  botId?: string
  /** Đang trả lời / gợi ý cho khách ở ít nhất một kênh. */
  answering: boolean
  /** Lý do chưa trả lời / cảnh báo; `fix.path` = màn cần mở. */
  reasons?: Array<{ code: string; message: string; fix?: { label: string; path: string | null; section?: string } | null; blocking?: boolean }>
  /** Kênh nhân viên AI đang thực sự phục vụ. */
  channels?: Array<{ id: string; name?: string | null; platform?: number; status?: string; connected?: boolean }>
  mode?: AiMode | string | null
  publishedVersion?: number | null
  hasUnpublishedChanges?: boolean
  /** Nền tảng / model / nguồn của bản đang chạy. */
  effective?: BotEffective | null
}

export type AiBotKind =
  | 'builtin'
  | 'openai_assistant'
  | 'coze_cn'
  | 'coze_intl'
  | 'dify'
  | 'gptbots'
  | 'webhook'
export type OnlineStatus = 'online' | 'busy' | 'offline'
export type AiBotInput = Partial<
  Omit<AiBot, 'id' | 'createdAt' | 'updatedAt' | 'hasWebhookToken' | 'webhookReplyUrl' | 'externalConfigBroken' | 'effective' | 'status'>
>
export interface OrgTeam {
  id: string
  name: string
}

// ── Query hooks ─────────────────────────────────────────────────────
export function useAiConfig() {
  return useApiQuery<AiConfig>(aiKeys.config, '/ai/config')
}

export function useChannelOverrides() {
  return useApiQuery<{ overrides: ChannelOverrides; channels: ChannelInfo[] }>(
    aiKeys.channelOverrides,
    '/ai/channel-overrides',
    undefined,
    // Kênh được kết nối ở màn Kết nối (mở ở tab mới) → quay lại tab là cập nhật.
    { refetchOnWindowFocus: true },
  )
}

export function useCustomModels() {
  return useApiQuery<{ customModels: CustomModels }>(aiKeys.customModels, '/ai/custom-models')
}

export function useAiBots() {
  return useApiQuery<{ bots: AiBot[] }>(aiKeys.bots, '/ai/bots')
}

export function useAiUsage(days: number) {
  return useApiQuery<AiUsage>(aiKeys.usage(days), '/ai/usage', { days })
}

export function useReplyRuns(limit = 50) {
  return useApiQuery<{ runs: ReplyRun[] }>(aiKeys.replyRuns(limit), '/ai/reply-runs', { limit })
}

export function useAiScenarios() {
  return useApiQuery<{ scenarios: ScenarioMeta[] }>(aiKeys.scenarios, '/ai/scenarios')
}

export function useKnowledgeGaps(status: GapStatus) {
  return useApiQuery<{ data: KnowledgeGap[]; total: number }>(
    aiKeys.knowledgeGaps(status),
    '/ai/knowledge-gaps',
    { status },
  )
}

/** "Đơn nháp do AI tạo" đang chờ khách xác nhận — xem/huỷ tay ở section-tools.tsx. */
export interface PendingOrderPayload {
  orderNo: string
  total: number
  typePay: 'COD' | 'Transfer'
  delivery: 'InShop' | 'ExpressDelivery'
  lines: { name: string; quantity: number }[]
  confirmUrl: string | null
  expiresAt: string
  botId: string
}
export interface PendingOrderAction {
  id: string
  conversationId: string | null
  contactId: string | null
  summary: string | null
  status: string
  payload: PendingOrderPayload
  createdAt: string
}
export function usePendingOrders() {
  return useApiQuery<{ data: PendingOrderAction[] }>(
    aiKeys.pendingOrders,
    '/ai/pending-actions',
    { type: 'create_order', status: 'awaiting_customer' },
  )
}
export async function cancelPendingOrder(id: string) {
  const { data } = await api.post(`/ai/pending-actions/${id}/cancel-order`)
  return data as { success: boolean }
}

// ── Mutation helpers (gọi trực tiếp qua `api`) ──────────────────────
export async function saveApiKey(provider: string, apiKey: string, skipVerify?: boolean) {
  const { data } = await api.put('/ai/api-key', { provider, apiKey, skipVerify })
  return data as { ok: boolean; verified: boolean }
}

export async function deleteApiKey(provider: string) {
  const { data } = await api.delete(`/ai/api-key/${provider}`)
  return data as { ok: boolean }
}

export async function updateAiConfig(body: Partial<{
  provider: string
  model: string
  maxDaily: number
  enabled: boolean
  taskOverrides: TaskOverrides
  defaultAiMode: AiMode
  autoReplyEnabled: boolean
  debounceSeconds: number
  prefilterKeywords: string | null
  groupRequireMention: boolean
  mentionNames: string | null
  verifyBeforeSend: boolean
}>) {
  const { data } = await api.put('/ai/config', body)
  return data as AiConfig
}

export async function saveChannelOverrides(overrides: ChannelOverrides) {
  const { data } = await api.put('/ai/channel-overrides', { overrides })
  return data as { overrides: ChannelOverrides }
}

export async function saveCustomModels(customModels: CustomModels) {
  const { data } = await api.put('/ai/custom-models', { customModels })
  return data as { customModels: CustomModels }
}

export async function createAiBot(input: AiBotInput) {
  const { data } = await api.post('/ai/bots', input)
  // webhookToken chỉ có khi tạo nhân viên AI kiểu Webhook — hiện đúng một lần.
  return data as { bot: AiBot; webhookToken?: string; warnings?: string[] }
}

/** Webhook: tạo mã truy cập mới (mã cũ hết hiệu lực ngay). */
export async function rotateAiBotToken(id: string) {
  const { data } = await api.post(`/ai/bots/${id}/rotate-token`)
  return data as { token: string; replyUrl: string }
}

/** "Kiểm tra kết nối" AI bên thứ ba — gửi "Xin chào" và nhận câu trả lời. */
export async function testAiBotConnection(body: {
  botId?: string
  kind: AiBotKind
  externalConfig: Record<string, string>
}) {
  const { data } = await api.post('/ai/bots/test-connection', body)
  return data as { ok: boolean; reply?: string; error?: string; latencyMs: number }
}

/** Phân nhóm (Team) của tổ chức — dùng cho trường "Phân nhóm" của nhân viên AI. */
export function useOrgTeams(enabled = true) {
  return useApiQuery<{ teams: OrgTeam[] }>(['teams'], '/teams', undefined, { enabled })
}

export async function updateAiBot(id: string, input: AiBotInput) {
  const { data } = await api.put(`/ai/bots/${id}`, input)
  return data as { bot: AiBot; warnings?: string[] }
}

export async function deleteAiBot(id: string) {
  const { data } = await api.delete(`/ai/bots/${id}`)
  return data as { deleted: boolean }
}

// Demo chat cho bot: tạo hội thoại sandbox rồi mô phỏng trả lời với botId
export async function createSandboxConversation(name: string) {
  const { data } = await api.post('/ai/simulate/conversation', { name, aiMode: 'suggest' })
  return data as { conversationId: string }
}

export async function simulateBotReply(conversationId: string, customerText: string, botId: string) {
  // includeTrace: để chat thử hiện "AI đã tra: Evotech · 3 sản phẩm".
  const { data } = await api.post('/ai/simulate/reply', { conversationId, customerText, botId, includeTrace: true })
  return data as {
    reply: string | null; runId?: string; handoff?: { should: boolean; reason?: string } | null
    trace?: Array<{ step: string; payload: unknown }>
  }
}

export async function fetchScenario(id: string) {
  const { data } = await api.get(`/ai/scenarios/${id}`)
  return data as { scenario: ScenarioDetail }
}

export async function createScenario(input: ScenarioInput) {
  const { data } = await api.post('/ai/scenarios', input)
  return data as { scenario: ScenarioDetail }
}

export async function updateScenario(id: string, input: Partial<ScenarioInput>) {
  const { data } = await api.put(`/ai/scenarios/${id}`, input)
  return data as { scenario: ScenarioDetail }
}

export async function deleteScenario(id: string) {
  const { data } = await api.delete(`/ai/scenarios/${id}`)
  return data as { deleted: boolean }
}

export async function fetchTrace(runId: string) {
  const { data } = await api.get('/ai/trace', { params: { runId } })
  return data as { traces: AiTrace[]; nextCursor: string | null }
}

export async function dismissGap(id: string) {
  const { data } = await api.post(`/ai/knowledge-gaps/${id}/dismiss`)
  return data as { success: boolean }
}

/**
 * Tạo mục kiến thức từ câu hỏi chưa trả lời được. `categoryId`: danh mục của mục — thiếu thì nhân
 * viên AI bị giới hạn danh mục KHÔNG thấy mục này (trước đây luôn thiếu).
 */
export async function resolveGap(id: string, content: string, title?: string, categoryId?: string | null, knowledgeBaseId?: string | null) {
  const { data } = await api.post(`/ai/knowledge-gaps/${id}/resolve`, {
    content, title, categoryId: categoryId || undefined, knowledgeBaseId: knowledgeBaseId || undefined,
  })
  return data as { success: boolean; entryId?: string }
}
